/**
 * Pay this invoice on the portal (piece 21; decisions 0201, 0239, 0372, 1650):
 * the amount, the rail, the card type and the surcharge, and the answers the
 * invoice page and the Checkout's return read. One copy, so the window that
 * draws the total and the server that opens the Checkout cannot disagree.
 */
import { roundHalfUpCents } from '../money';
import { t } from '../strings';
import { cardSurchargeRates, type CardType } from './cardSurcharge';

/** The two choices the customer is offered; Bank transfer is the ACH rail through the same Checkout. */
export const PAY_RAILS = ['Card', 'Bank'] as const;
export type PayRail = (typeof PAY_RAILS)[number];

/** 🔒 What the invoice page answers a portal login where Pay this invoice is offered, and null everywhere else. */
export interface PayOnlineOffer {
  openBalanceCents: number;
  currencyCode: string;
  /** The rails a gateway is reachable on, in the order the window offers them. */
  rails: PayRail[];
  /** The stored credit-card rate; debit and prepaid stand at zero (decision 1620). */
  creditSurchargePercent: number;
}

/** What Continue to payment sends. */
export interface PayCheckoutRequest {
  amountCents: number;
  rail: PayRail;
  /** Asked before the card on the Card rail (decision 0372), absent on Bank. */
  cardType?: CardType | null;
}

export interface PayCheckoutAnswer {
  /** The processor's own hosted page, which the browser is sent to. */
  url: string;
}

/**
 * 🔒 Where the return stands (decision 1650): the processor says paid and no
 * payment is recorded yet, the payment is recorded, a bank transfer is still
 * settling, or the Checkout was left unpaid.
 */
export type PayReturnState = 'confirming' | 'paid' | 'processing' | 'notCompleted';

export interface PayReturnAnswer {
  state: PayReturnState;
}

/** The query keys the Checkout's return carries back to the invoice page. */
export const PAY_RETURN_SESSION_PARAM = 'checkout';
export const PAY_RETURN_GATEWAY_PARAM = 'via';
/** Our own placeholder for the session id in the return address; each adapter swaps in its processor's. */
export const PAY_RETURN_SESSION_PLACEHOLDER = '{SESSION}';

/** The browser paths, relative to the API root, under the invoice they pay. */
export function invoicePayPath(invoicesPath: string, invoiceId: string): string {
  return `${invoicesPath}/${encodeURIComponent(invoiceId)}/pay`;
}
export function invoicePayReturnPath(invoicesPath: string, invoiceId: string, session: string, gateway: string): string {
  return `${invoicePayPath(invoicesPath, invoiceId)}/return?${new URLSearchParams({ session, via: gateway }).toString()}`;
}

/** The percentage a card type carries: the stored credit rate, and zero for debit and prepaid. */
export function cardTypePercent(cardType: CardType, creditPercent: number): number {
  return cardSurchargeRates(creditPercent).find((rate) => rate.cardType === cardType)?.percent ?? 0;
}

/** The surcharge line's words, its rate beside them, as the window draws it beneath the amount. */
export function paySurchargeLabel(percent: number): string {
  return `${t('invoice.pay.surcharge')} (${percent}%)`;
}

/** The surcharge on an amount at a percentage, rounded half up to the cent (decision 0239). */
export function cardSurchargeCents(amountCents: number, percent: number): number {
  if (!(percent > 0) || !(amountCents > 0)) return 0;
  return roundHalfUpCents((amountCents * percent) / 100);
}

/** The surcharge and the total a choice comes to: Card at its type's rate, Bank at none. */
export function payTotals(choice: { amountCents: number; rail: PayRail; cardType?: CardType | null }, creditPercent: number): { surchargeCents: number; totalCents: number } {
  const percent = choice.rail === 'Card' && choice.cardType ? cardTypePercent(choice.cardType, creditPercent) : 0;
  const surchargeCents = cardSurchargeCents(choice.amountCents, percent);
  return { surchargeCents, totalCents: choice.amountCents + surchargeCents };
}

/** 🔨 The refusal an amount earns, or null: whole cents above zero and never past what the invoice still owes. */
export function payAmountProblem(amountCents: unknown, openBalanceCents: number): string | null {
  if (typeof amountCents !== 'number' || !Number.isInteger(amountCents) || amountCents <= 0) return 'Enter an amount above zero to pay.';
  if (amountCents > openBalanceCents) return 'The amount is more than this invoice still owes; enter the open balance or less.';
  return null;
}

/** 🔨 The refusal a choice of rail earns, or null: an offered rail, and a card type on the Card rail. */
export function payChoiceProblem(choice: { rail?: unknown; cardType?: unknown }, rails: readonly PayRail[]): string | null {
  if (typeof choice.rail !== 'string' || !(rails as readonly string[]).includes(choice.rail)) return 'Choose how to pay: one of the ways this invoice offers.';
  if (choice.rail === 'Card' && !['Credit', 'Debit', 'Prepaid'].includes(String(choice.cardType))) return 'Choose the kind of card before continuing: credit, debit or prepaid.';
  return null;
}
